import { memo, useState, useEffect, ChangeEvent } from "react";
import {
  Box,
  Typography,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  Divider,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AppDialog from "../common/AppDialog.tsx";

// Types and Interfaces
export interface VariantTypeRow {
  VARIANTCODE?: string | number;
  variantcode?: string | number;
  code?: string | number;
  VARIANTNAME?: string;
  variantname?: string;
  label?: string;
  [key: string]: any;
}

export interface AddVariantTypeDialogProps {
  open: boolean;
  onClose: () => void;
  existingVariantTypes?: VariantTypeRow[];
  loading?: boolean;
  variantName?: string;
  onVariantNameChange: (value: string) => void;
  onAdd: () => void;
  /** Kept for call-site compatibility; buttons use AppDialog Theme styles. */
  styles?: Record<string, any>;
}

/**
 * Reusable Add Variant Type dialog: table of existing types with search, duplicate validation, and form to add new.
 */
const AddVariantTypeDialog = memo<AddVariantTypeDialogProps>(
  ({
    open,
    onClose,
    existingVariantTypes = [],
    loading = false,
    variantName = "",
    onVariantNameChange,
    onAdd,
  }) => {
    const [search, setSearch] = useState<string>("");

    useEffect(() => {
      if (!open) {
        setSearch("");
      }
    }, [open]);

    const handleCancel = (): void => {
      onVariantNameChange("");
      onClose();
    };

    const getName = (row: VariantTypeRow): string =>
      (row.VARIANTNAME ?? row.variantname ?? row.label ?? "").toString().trim();

    const q: string = (search || "").trim().toLowerCase();
    const filtered: VariantTypeRow[] =
      q && existingVariantTypes.length > 0
        ? existingVariantTypes.filter((row: VariantTypeRow) => {
            const name = getName(row);
            return name.toLowerCase().includes(q);
          })
        : existingVariantTypes;

    const isDuplicate: boolean =
      !!(variantName || "").trim() &&
      existingVariantTypes.some(
        (row: VariantTypeRow) =>
          getName(row).toLowerCase() === (variantName || "").trim().toLowerCase()
      );

    const addDisabled: boolean = !(variantName || "").trim() || !!isDuplicate;

    const handleSearchChange = (e: ChangeEvent<HTMLInputElement>): void => {
      setSearch(e.target.value);
    };

    return (
      <AppDialog
        open={open}
        onClose={handleCancel}
        title="Add Variant Type"
        maxWidth="sm"
        fullWidth
        cancelLabel="Cancel"
        confirmLabel="Add"
        confirmColor="primary"
        confirmDisabled={addDisabled}
        confirmStartIcon={<AddCircleOutlineIcon />}
        onConfirm={onAdd}
      >
        <Box sx={{ minHeight: 380 }}>
          {loading ? (
            <Box sx={{ py: 2, textAlign: "center", minHeight: 280 }}>
              <Typography variant="body2" color="text.secondary">
                Loading variant types…
              </Typography>
            </Box>
          ) : null}

          {!loading && existingVariantTypes.length > 0 ? (
            <Box sx={{ mb: 2, minHeight: 280 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                Existing variant types
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder="Search variants…"
                value={search}
                onChange={handleSearchChange}
                sx={{ mb: 1 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ height: 220, borderRadius: 1, overflow: "auto" }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Variant name</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.length > 0 ? (
                      filtered.map((row: VariantTypeRow, idx: number) => (
                        <TableRow
                          key={row.VARIANTCODE ?? row.variantcode ?? row.code ?? idx}
                        >
                          <TableCell sx={{ fontWeight: 400, fontSize: "inherit" }}>
                            {getName(row) || ""}
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell align="center" sx={{ py: 3 }}>
                          <Typography variant="body2" color="text.secondary">
                            No matching variants found
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ) : null}

          {!loading && (!existingVariantTypes || existingVariantTypes.length === 0) ? (
            <Box
              sx={{
                minHeight: 280,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No existing variant types
              </Typography>
            </Box>
          ) : null}

          <Divider sx={{ my: 2 }} />

          <Box sx={{ mt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Variant Name"
                  value={variantName}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    onVariantNameChange(e.target.value)
                  }
                  required
                  error={!!isDuplicate}
                  helperText={isDuplicate ? "This variant name already exists." : ""}
                  FormHelperTextProps={{ sx: { fontSize: "12px", color: "error.main" } }}
                  inputProps={{ maxLength: 150 }}
                  autoFocus
                />
              </Grid>
            </Grid>
          </Box>
        </Box>
      </AppDialog>
    );
  }
);

AddVariantTypeDialog.displayName = "AddVariantTypeDialog";

export default AddVariantTypeDialog;
